import * as Yup from 'yup';
import {
  formatInternationalPhoneNumberE164,
  isValidInternationalPhoneNumber,
} from '@rsos/utils/internationalPhoneNumberUtils';

/* types */
export interface ShareEntry {
  value: string;
  isValid: boolean;
  id?: string;
}

export interface ShareFormValues {
  email: string;
  emailsCumulative: ShareEntry[];
  phoneNumber: string;
  phoneNumbersCumulative: ShareEntry[];
  consentShare: boolean;
}

/* constants */
export const CALL_FLOW = 'alert_share';
export const CALL_FLOW_NEXT = 'alert_share_v2';
export const SHARE_SUCCESS_MESSAGE = 'Event data sent.';
export const SHARE_ERROR_MESSAGE =
  'Event data failed to share. Please try again.';

export const initialValues = {
  email: '',
  emailsCumulative: [],
  phoneNumber: '',
  phoneNumbersCumulative: [],
  consentShare: false,
};

/* Validators */
export const validationSchema = Yup.object().shape({
  emailsCumulative: Yup.array().test(
    'isEmailsValid',
    'Invalid email address.',
    function (value: ShareEntry[] | undefined) {
      const { path, createError } = this;
      const result = value?.find(v => !v.isValid)
        ? createError({ path, message: 'Invalid email(s) address.' })
        : true;
      return result;
    },
  ),
  phoneNumbersCumulative: Yup.array().test(
    'isPhoneNumberValid',
    'Invalid phone number.',
    function (value: ShareEntry[] | undefined) {
      const { path, createError } = this;
      const result = value?.find(v => !v.isValid)
        ? createError({ path, message: 'Invalid phone number(s).' })
        : true;
      return result;
    },
  ),
  consentShare: Yup.bool().oneOf([true], 'Consent for sharing is required'),
});

export const phoneNumberValidator = (val: string): boolean => {
  const sanitizedValue = val.replace(/[()-\s]/g, '');
  return isValidInternationalPhoneNumber(sanitizedValue);
};

/* Function to remove duplicates from an array */
const getUniqueArrayValues = (array: string[]) => {
  return array.filter((value, index) => array.indexOf(value) === index);
};

export const getSanitizedUniqueValues = (
  values: Partial<ShareFormValues>,
  type: keyof ShareFormValues,
): string[] => {
  const entries = values?.[type];
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }
  switch (type) {
    case 'phoneNumbersCumulative': {
      const sanitizedValues = entries
        .map(n => formatInternationalPhoneNumberE164(n.value))
        .filter((n): n is string => n !== null);
      return getUniqueArrayValues(sanitizedValues);
    }
    case 'emailsCumulative': {
      const sanitizedValues = entries.map(n => n.value);
      return getUniqueArrayValues(sanitizedValues);
    }
    default:
      return [];
  }
};
